(() => {
  'use strict';

  const SELECTOR = '[data-npq-selector]';
  const OPTION = '[data-npq-option]';

  function formatMoney(cents, moneyFormat) {
    const value = Number.parseInt(cents, 10);

    if (!Number.isFinite(value)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, moneyFormat);
    }

    const format = moneyFormat || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';

    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const amount = Number(number);
      if (!Number.isFinite(amount)) return '0';

      const parts = (amount / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };

    let output;

    switch (placeholder) {
      case 'amount_no_decimals':
        output = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        output = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        output = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        output = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        output = withDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        output = withDelimiters(value, 2, "'", '.');
        break;
      default:
        output = withDelimiters(value, 2);
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, output);
  }

  function getProductForm(selector) {
    const formId = selector.dataset.productFormId;
    return formId ? document.getElementById(formId) : null;
  }

  function getOrCreateQuantityInput(form) {
    if (!form) return null;

    const namedControl = form.elements ? form.elements.namedItem('quantity') : null;

    if (namedControl instanceof HTMLInputElement || namedControl instanceof HTMLSelectElement) {
      return namedControl;
    }

    const existing = form.querySelector('[data-npq-form-quantity]');
    if (existing) return existing;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    input.value = '1';
    input.setAttribute('data-npq-form-quantity', '');
    form.appendChild(input);

    return input;
  }

  function getSelectedOption(selector) {
    return selector.querySelector(`${OPTION}:checked:not(:disabled)`) ||
      selector.querySelector(`${OPTION}:not(:disabled)`);
  }

  function updateButtonPrices(selector, discountedTotal, moneyFormat) {
    const sectionId = selector.dataset.sectionId;
    if (!sectionId) return;

    const formatted = `${formatMoney(discountedTotal, moneyFormat)} •`;
    const mainPrice = document.getElementById(`main-atc-price-${sectionId}`);
    const stickyPrice = document.getElementById(`sticky-atc-price-${sectionId}`);

    if (mainPrice) mainPrice.textContent = formatted;
    if (stickyPrice) stickyPrice.textContent = formatted;
  }

  function syncSelector(selector, selectedOption = getSelectedOption(selector)) {
    if (!selector || !selectedOption || selectedOption.disabled) return;

    const quantity = Number.parseInt(selectedOption.dataset.quantity, 10);
    const discount = Number.parseInt(selectedOption.dataset.discount, 10) || 0;
    const unitPrice = Number.parseInt(selector.dataset.unitPrice, 10);

    if (!Number.isInteger(quantity) || quantity < 1 || !Number.isFinite(unitPrice)) return;

    const regularTotal = unitPrice * quantity;
    const discountedTotal = Math.round(regularTotal * (100 - discount) / 100);
    const discountedUnitPrice = Math.round(discountedTotal / quantity);
    const savings = regularTotal - discountedTotal;
    const moneyFormat = selector.dataset.moneyFormat || '${{amount}}';

    selector.querySelectorAll(OPTION).forEach((option) => {
      const row = option.closest('[data-npq-option-label]');
      const active = option === selectedOption;
      option.checked = active;
      if (row) row.classList.toggle('is-selected', active);
    });

    const form = getProductForm(selector);
    const quantityInput = getOrCreateQuantityInput(form);

    if (quantityInput) {
      /*
       * Do not dispatch input/change events here. Some themes and apps treat
       * those events as cart-update requests, which can create update errors.
       * Shopify reads this value normally when the product form is submitted.
       */
      quantityInput.value = String(quantity);
    }

    const summaryQuantity = selector.querySelector('[data-npq-summary-quantity]');
    const summaryRegular = selector.querySelector('[data-npq-summary-regular]');
    const summaryTotal = selector.querySelector('[data-npq-summary-total]');
    const summaryUnit = selector.querySelector('[data-npq-summary-unit]');
    const summarySavings = selector.querySelector('[data-npq-summary-savings]');

    if (summaryQuantity) {
      summaryQuantity.textContent = quantity === 1 ? '1 can' : `${quantity} cans`;
    }

    if (summaryTotal) {
      summaryTotal.textContent = formatMoney(discountedTotal, moneyFormat);
    }

    if (summaryUnit) {
      summaryUnit.textContent = `${formatMoney(discountedUnitPrice, moneyFormat)}/can`;
    }

    if (discount > 0 && savings > 0) {
      if (summaryRegular) {
        summaryRegular.textContent = formatMoney(regularTotal, moneyFormat);
        summaryRegular.hidden = false;
      }

      if (summarySavings) {
        summarySavings.textContent = `You save ${formatMoney(savings, moneyFormat)} on this selection.`;
        summarySavings.hidden = false;
      }
    } else {
      if (summaryRegular) {
        summaryRegular.textContent = '';
        summaryRegular.hidden = true;
      }

      if (summarySavings) {
        summarySavings.textContent = '';
        summarySavings.hidden = true;
      }
    }

    updateButtonPrices(selector, discountedTotal, moneyFormat);
  }

  function initializeSelector(selector) {
    if (!(selector instanceof HTMLElement)) return;

    const selected = getSelectedOption(selector);
    if (selected) syncSelector(selector, selected);
    selector.dataset.npqInitialized = 'true';
  }

  function initializeAll(container = document) {
    if (container instanceof Element && container.matches(SELECTOR)) {
      initializeSelector(container);
    }

    container.querySelectorAll?.(SELECTOR).forEach(initializeSelector);
  }

  document.addEventListener('change', (event) => {
    const option = event.target.closest?.(OPTION);
    if (!option || option.disabled || !option.checked) return;

    const selector = option.closest(SELECTOR);
    if (selector) syncSelector(selector, option);
  });

  /*
   * Final safety sync immediately before any product-form submission,
   * including the normal Add to Cart button and sticky Add to Cart button.
   */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.id) return;

    document.querySelectorAll(SELECTOR).forEach((selector) => {
      if (selector.dataset.productFormId === form.id) {
        syncSelector(selector);
      }
    });
  }, true);

  function start() {
    initializeAll();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) initializeAll(node);
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
